/**
 * ReviewList Component
 * Product reviews display with filtering and sorting
 * BP-08-04: Marketplace Frontend (Family)
 */

import React, { useState } from 'react';
import { 
  ThumbsUp, 
  CheckCircle2, 
  ImageIcon,
  ChevronDown,
  MessageSquare 
} from 'lucide-react';
import { StarRating } from './StarRating';
import { useInfiniteReviews, useMarkReviewHelpful } from '@/features/marketplace/hooks/useMarketplace';
import type { ReviewListProps, ReviewFilters, ProductReview } from '@/features/marketplace/types/marketplaceTypes';
import { formatDistanceToNow } from 'date-fns';

const SORT_OPTIONS = [
  { value: 'newest', label: 'Most Recent' },
  { value: 'helpful', label: 'Most Helpful' },
  { value: 'rating_high', label: 'Highest Rated' },
  { value: 'rating_low', label: 'Lowest Rated' },
] as const;

interface ReviewCardProps {
  review: ProductReview;
  productId: string;
}

const ReviewCard: React.FC<ReviewCardProps> = ({ review, productId }) => {
  const { mutate: markHelpful, isPending } = useMarkReviewHelpful(productId);
  
  const handleMarkHelpful = () => {
    if (!isPending) {
      markHelpful(review.id);
    }
  };
  
  return (
    <div className="py-6 border-b border-warm-gray/10 last:border-b-0">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-center gap-3">
          {/* Avatar */}
          <div className="
            w-10 h-10 rounded-full
            bg-sage/20 text-sage-dark
            flex items-center justify-center
            font-semibold text-sm
          ">
            {review.userName.charAt(0).toUpperCase()}
          </div>
          
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-navy">{review.userName}</span>
              {review.verifiedPurchase && (
                <span className="
                  flex items-center gap-1
                  text-xs font-medium text-sage-dark
                ">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Verified
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <StarRating rating={review.rating} size="sm" />
              <span className="text-sm text-warm-gray">
                {formatDistanceToNow(new Date(review.createdAt), { addSuffix: true })}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Title */}
      {review.title && (
        <h4 className="font-semibold text-navy mb-2">{review.title}</h4>
      )}
      
      {/* Content */}
      <p className="text-navy-light leading-relaxed mb-4">
        {review.content}
      </p>
      
      {/* Pros/Cons */}
      {(review.pros.length > 0 || review.cons.length > 0) && (
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          {review.pros.length > 0 && (
            <div className="bg-sage/5 rounded-lg p-3">
              <h5 className="text-sm font-semibold text-sage-dark mb-2">Pros</h5>
              <ul className="space-y-1">
                {review.pros.map((pro, index) => (
                  <li key={index} className="text-sm text-navy-light flex items-start gap-2">
                    <span className="text-sage mt-1">+</span>
                    {pro}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {review.cons.length > 0 && (
            <div className="bg-terracotta/5 rounded-lg p-3">
              <h5 className="text-sm font-semibold text-terracotta mb-2">Cons</h5>
              <ul className="space-y-1">
                {review.cons.map((con, index) => (
                  <li key={index} className="text-sm text-navy-light flex items-start gap-2">
                    <span className="text-terracotta mt-1">−</span>
                    {con}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      
      {/* Images */}
      {review.images.length > 0 && (
        <div className="flex gap-2 mb-4 overflow-x-auto">
          {review.images.map((image, index) => (
            <img
              key={index}
              src={image}
              alt={`Review image ${index + 1}`}
              className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
            />
          ))}
        </div>
      )}
      
      {/* Vendor Response */}
      {review.vendorResponse && (
        <div className="bg-cream rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare className="w-4 h-4 text-sage" />
            <span className="font-semibold text-sm text-navy">Vendor Response</span>
          </div>
          <p className="text-sm text-navy-light">{review.vendorResponse}</p>
        </div>
      )}
      
      {/* Helpful button */}
      <button
        onClick={handleMarkHelpful}
        disabled={isPending}
        className={`
          inline-flex items-center gap-2
          px-3 py-1.5 rounded-full
          text-sm font-medium
          transition-colors duration-200
          ${review.isHelpfulByCurrentUser
            ? 'bg-sage/10 text-sage-dark'
            : 'bg-cream hover:bg-cream-dark text-warm-gray hover:text-navy'
          }
          disabled:opacity-50 disabled:cursor-not-allowed
        `}
      >
        <ThumbsUp className="w-4 h-4" />
        Helpful ({review.helpfulCount})
      </button>
    </div>
  );
};

export const ReviewList: React.FC<ReviewListProps> = ({
  productId,
  initialFilters,
}) => {
  const [filters, setFilters] = useState<ReviewFilters>(initialFilters || {
    sortBy: 'helpful',
  });
  const [showFilters, setShowFilters] = useState(false);
  
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteReviews(productId, filters);
  
  const reviews = data?.pages.flatMap((page) => page.items) || [];
  const totalReviews = data?.pages[0]?.total || 0;
  
  const handleSortChange = (sortBy: ReviewFilters['sortBy']) => {
    setFilters((prev) => ({ ...prev, sortBy }));
  };
  
  const handleFilterToggle = (key: keyof ReviewFilters) => {
    setFilters((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };
  
  if (isLoading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse py-6 border-b border-warm-gray/10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-cream-dark" />
              <div className="space-y-2">
                <div className="w-32 h-4 bg-cream-dark rounded" />
                <div className="w-24 h-3 bg-cream-dark rounded" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="w-48 h-5 bg-cream-dark rounded" />
              <div className="w-full h-16 bg-cream-dark rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  
  if (isError) {
    return (
      <div className="text-center py-8 text-warm-gray">
        Unable to load reviews. Please try again.
      </div>
    );
  }
  
  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-6">
        <h3 className="font-display font-semibold text-lg text-navy">
          Reviews ({totalReviews.toLocaleString()})
        </h3>
        
        <div className="flex items-center gap-3">
          {/* Filter toggles */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleFilterToggle('verifiedOnly')}
              className={`
                px-3 py-1.5 rounded-full text-sm font-medium
                transition-colors duration-200
                ${filters.verifiedOnly
                  ? 'bg-sage text-white'
                  : 'bg-cream hover:bg-cream-dark text-navy-light'
                }
              `}
            >
              Verified Only
            </button>
            
            <button
              onClick={() => handleFilterToggle('withImages')}
              className={`
                px-3 py-1.5 rounded-full text-sm font-medium
                flex items-center gap-1.5
                transition-colors duration-200
                ${filters.withImages
                  ? 'bg-sage text-white'
                  : 'bg-cream hover:bg-cream-dark text-navy-light'
                }
              `}
            >
              <ImageIcon className="w-4 h-4" />
              With Photos
            </button>
          </div>
          
          {/* Sort dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="
                flex items-center gap-2
                px-3 py-1.5 rounded-lg
                bg-cream hover:bg-cream-dark
                text-sm font-medium text-navy-light
                transition-colors duration-200
              "
            >
              {SORT_OPTIONS.find((o) => o.value === filters.sortBy)?.label || 'Sort'}
              <ChevronDown className="w-4 h-4" />
            </button>
            
            {showFilters && (
              <>
                <div 
                  className="fixed inset-0 z-10"
                  onClick={() => setShowFilters(false)}
                />
                <div className="
                  absolute right-0 top-full mt-2 z-20
                  w-48 py-2
                  bg-white rounded-lg shadow-medium
                  border border-warm-gray/10
                ">
                  {SORT_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        handleSortChange(option.value);
                        setShowFilters(false);
                      }}
                      className={`
                        w-full px-4 py-2 text-left text-sm
                        transition-colors duration-200
                        ${filters.sortBy === option.value
                          ? 'bg-sage/10 text-sage-dark font-medium'
                          : 'text-navy-light hover:bg-cream'
                        }
                      `}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Reviews */}
      {reviews.length === 0 ? (
        <div className="text-center py-12">
          <MessageSquare className="w-12 h-12 text-warm-gray/50 mx-auto mb-3" />
          <p className="text-warm-gray">No reviews yet. Be the first to review!</p>
        </div>
      ) : (
        <div>
          {reviews.map((review) => (
            <ReviewCard
              key={review.id}
              review={review}
              productId={productId}
            />
          ))}
          
          {/* Load more */}
          {hasNextPage && (
            <div className="text-center pt-6">
              <button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="
                  px-6 py-2.5 rounded-lg
                  bg-cream hover:bg-cream-dark
                  text-navy font-medium
                  transition-colors duration-200
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                {isFetchingNextPage ? 'Loading...' : 'Load More Reviews'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReviewList;

